/**
 * Track 4 — SheetAssetMarkers hook.
 *
 * Mirrors the SheetOverlayMarkers pattern: returns `{ pins, renderPin,
 * drawer, refresh, placementActive, setPlacementActive,
 * placementType, setPlacementType }`. SheetViewer merges these pins
 * into the existing canvas pin contract.
 *
 * When `placementActive` is true, the marker layer intercepts the next
 * tap on the sheet to place a new asset of `placementType`.
 *
 * The drawer is the "What's Happening Here" Asset drawer with context
 * (photos / voice notes / stories / revisions / conflicts).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Circle, Disc, ChevronRight, X, Trash2, MapPin, Loader2, Camera, Mic,
  ShieldAlert, MessageSquareWarning, Send, ListChecks, ExternalLink, Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import { confirm } from "@/lib/confirm";


export const ASSET_TYPES = [
  ["MANHOLE",              "Manhole"],
  ["INLET",                "Inlet"],
  ["DRAINAGE_STRUCTURE",   "Drainage structure"],
  ["PIPE",                 "Pipe"],
  ["VALVE",                "Valve"],
  ["WATER_METER",          "Water meter"],
  ["HYDRANT",              "Hydrant"],
  ["SIGN",                 "Sign"],
  ["LIGHT_POLE",           "Light pole"],
  ["PULL_BOX",             "Pull box"],
  ["HANDHOLE",             "Handhole"],
  ["ELECTRICAL_STRUCTURE", "Electrical structure"],
  ["UTILITY_STRUCTURE",    "Utility structure"],
  ["ROADWAY_STRUCTURE",    "Roadway structure"],
  ["SURVEY_POINT",         "Survey point"],
  ["OTHER",                "Other"],
];

// Construction-language colour mapping. Five tones only.
const TONES = {
  active:            "#475569", // Neutral Gray
  needs_review:      "#f97316", // Attention Orange
  waiting_for_field: "#f97316", // Attention Orange
  conflict:          "#dc2626", // Trust Red
  completed:         "#16a34a", // Completed Green
  removed:           "#94a3b8", // soft gray
};
const ASSET_TONE = "#0ea5e9";   // Operational Blue — non-status default for marker icon


export default function useSheetAssetMarkers({ sheetId, projectId, scale = 1, onConsumePlacementClick }) {
  const [assets, setAssets] = useState([]);
  const [active, setActive] = useState(null);
  const [placementType, setPlacementType] = useState(null);
  const placementActive = !!placementType;

  const refresh = useCallback(async () => {
    if (!sheetId) return;
    try {
      const r = await api.get(`/sheets/${sheetId}/assets`);
      setAssets(r.data?.items || []);
    } catch { /* silent */ }
  }, [sheetId]);

  useEffect(() => { refresh(); }, [refresh]);

  // React to story rebuilds — backend already recomputes asset status,
  // so we re-fetch the asset list to get fresh labels.
  useEffect(() => {
    const h = () => refresh();
    window.addEventListener("forgedops:field-stories-changed", h);
    window.addEventListener("forgedops:assets-changed", h);
    return () => {
      window.removeEventListener("forgedops:field-stories-changed", h);
      window.removeEventListener("forgedops:assets-changed", h);
    };
  }, [refresh]);

  // Place a new asset given normalized coordinates.
  const placeAt = useCallback(async (nx, ny) => {
    if (!placementType || !sheetId) return;
    try {
      const r = await api.post(`/sheets/${sheetId}/assets`, {
        asset_type: placementType, nx, ny,
      });
      const placed = r.data;
      confirm.raw?.success?.(`${placed.asset_type_label || "Asset"} placed`);
      window.dispatchEvent(new Event("forgedops:assets-changed"));
      setPlacementType(null);
      refresh();
      setActive({ asset_id: placed.id });
    } catch (e) {
      // Iter 179 — surface backend rejection with retry.
      confirm.saveFailed(`Couldn't place ${placementType.toLowerCase()}`, {
        error: e,
        retry: () => placeAt(nx, ny),
      });
    }
  }, [placementType, sheetId, refresh]);

  // Pin contract — each asset is a non-clustered pin at (nx, ny).
  // We deliberately skip clustering for assets so the operator can
  // always see and tap exactly what they placed.
  const pins = useMemo(() => assets.map((a) => ({
    id: `m_asset_${a.id}`,
    x: a.nx, y: a.ny,
    _asset: a,
  })), [assets]);

  const renderPin = useCallback((p) => {
    const a = p._asset;
    const tone = TONES[a.status] || TONES.active;
    return (
      <button
        type="button"
        data-testid={`sheet-asset-marker-${a.id}`}
        data-pin-id={p.id}
        data-viewer-overlay="1"
        data-overlay-type="asset-marker"
        data-overlay-id={a.id}
        data-overlay-anchor-nx={a.nx}
        data-overlay-anchor-ny={a.ny}
        onClick={(e) => { e.stopPropagation(); setActive({ asset_id: a.id }); }}
        className="absolute -translate-x-1/2 -translate-y-1/2 inline-flex items-center justify-center"
        style={{
          // ≥44px touch target wrapper (transparent), 22px visible disc.
          width: 44, height: 44,
          pointerEvents: "auto",
          touchAction: "manipulation",
        }}
        aria-label={`${a.asset_type_label || a.asset_type} · ${a.status_label || a.status}`}
      >
        <span
          className="inline-flex items-center justify-center rounded-md border-2 shadow"
          style={{
            width: 22, height: 22,
            background: "rgba(255,255,255,0.95)",
            borderColor: tone,
            color: tone,
          }}
        >
          <Disc size={10} />
        </span>
      </button>
    );
  }, []);

  const drawer = (
    <AssetDrawer
      active={active}
      onClose={() => setActive(null)}
      onChanged={refresh}
      projectId={projectId}
      sheetId={sheetId}
    />
  );

  // Placement banner shown across the top of the canvas when active.
  const placementBanner = placementActive ? (
    <div
      className="fixed left-1/2 -translate-x-1/2 top-3 z-30 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--fop-cta-bg)] text-white shadow-lg"
      data-testid="sheet-asset-placement-banner"
    >
      <MapPin size={14} />
      <span className="text-[12px] font-semibold">
        Tap the drawing to place {labelFor(placementType)}
      </span>
      <button
        type="button"
        onClick={() => setPlacementType(null)}
        className="ml-2 text-white/80 hover:text-white"
        data-testid="sheet-asset-placement-cancel"
        aria-label="Cancel placement"
      ><X size={14} /></button>
    </div>
  ) : null;

  return {
    pins, renderPin, drawer, refresh,
    assetCount: assets.length,
    placementType, setPlacementType, placementActive,
    placeAt,
    placementBanner,
  };
}


function labelFor(t) {
  return (ASSET_TYPES.find(([k]) => k === t) || ["", "asset"])[1].toLowerCase();
}


// ===========================================================================
// AssetDrawer — "What's Happening Here"
// ===========================================================================
function AssetDrawer({ active, onClose, onChanged }) {
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyDelete, setBusyDelete] = useState(false);
  const assetId = active?.asset_id;

  useEffect(() => {
    if (!assetId) { setContext(null); return; }
    let alive = true;
    setLoading(true);
    api.get(`/assets/${assetId}/context`)
      .then((r) => { if (alive) setContext(r.data); })
      .catch(() => { if (alive) setContext(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [assetId]);

  if (!active) return null;
  const a = context?.asset;
  const tone = a ? (TONES[a.status] || TONES.active) : TONES.active;

  return (
    <div
      className="fixed inset-y-0 right-0 w-full sm:w-[440px] z-40 flex flex-col border-l border-[var(--fop-border)]"
      data-testid="sheet-asset-drawer"
      style={{
        // Iter 178.2 — Layered drawer surface. Matches the trust-bar
        // popover + palette dropdown so every floating chrome element
        // shares one visual language. The subtle top gradient gives the
        // drawer a "raised panel" feel instead of a flat sheet.
        background: "linear-gradient(180deg, var(--fop-surface) 0%, var(--fop-surface-2) 100%)",
        boxShadow: "-24px 0 48px rgba(0,0,0,0.35), inset 1px 0 0 var(--fop-border)",
        pointerEvents: "auto",
      }}
    >
      <header
        className="px-4 h-12 border-b border-[var(--fop-border)] flex items-center gap-2 shrink-0"
        style={{
          background: "linear-gradient(180deg, var(--fop-surface-2) 0%, var(--fop-surface) 100%)",
          boxShadow: "inset 0 -1px 0 var(--fop-border)",
        }}
      >
        <span
          aria-hidden="true"
          className="inline-block w-1.5 h-4 rounded-sm bg-[var(--fop-primary)]"
        />
        <div className="flex-1 min-w-0">
          <div
            className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-text-3)]"
            data-testid="sheet-asset-drawer-label"
          >
            Asset · What&apos;s Happening Here
          </div>
          <div className="text-[12px] text-[var(--fop-text)] font-semibold truncate leading-tight">
            {a ? (a.asset_type_label || a.asset_type) : loading ? "Loading…" : "Asset"}
            {a?.drawing_reference ? ` · ${a.drawing_reference}` : ""}
          </div>
        </div>
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="sheet-asset-drawer-close"
          className="w-9 h-9 rounded-md flex items-center justify-center text-[var(--fop-text-2)] hover:text-white hover:bg-[var(--fop-surface-3)] transition-colors"
        >
          <X size={15} />
        </button>
      </header>
      {loading && (
        <div className="p-6 text-[13px] text-[var(--fop-text-3)] flex items-center gap-2">
          <Loader2 className="animate-spin" size={14} /> Loading…
        </div>
      )}
      {!loading && a && (
        <div className="flex-1 overflow-y-auto p-4" data-testid="sheet-asset-drawer-body">
          <div className="flex items-start gap-3 mb-4 pb-4 border-b border-[var(--fop-border)]">
            <div
              className="mt-0.5 inline-flex items-center justify-center rounded-lg shrink-0"
              style={{
                width: 40, height: 40,
                background: `${tone}18`,
                color: tone,
                border: `1px solid ${tone}55`,
              }}
            >
              <Disc size={18} strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div
                className="text-[15px] font-semibold text-[var(--fop-text)] truncate leading-tight"
                data-testid="sheet-asset-drawer-title"
              >
                {a.asset_type_label || a.asset_type}
                {a.drawing_reference ? ` · ${a.drawing_reference}` : ""}
              </div>
              <div
                className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-[0.18em] font-bold"
                style={{ background: `${tone}14`, color: tone, border: `1px solid ${tone}55` }}
                data-testid="sheet-asset-drawer-status"
              >
                {a.status_label || a.status}
              </div>
            </div>
          </div>

          <dl className="text-[12px] grid grid-cols-[110px_1fr] gap-y-1.5 mb-4">
            <Row k="Sheet"     v={a.sheet_number || a.sheet_id} />
            <Row k="Asset id"  v={a.id} />
            <Row k="Placed by" v={a.created_by_name} />
            <Row k="Placed"    v={_fmt(a.created_at)} />
            <Row k="Updated"   v={_fmt(a.updated_at)} />
            {a.notes && <Row k="Notes" v={a.notes} />}
          </dl>

          {/* Counts */}
          <section className="mb-4" data-testid="sheet-asset-drawer-counts">
            <Heading icon={<Clock size={11} />} label="Operational context" />
            <div className="grid grid-cols-3 gap-1.5">
              <Stat icon={<Camera size={12} />} label="Photos" n={context?.counts?.photos} testid="sheet-asset-stat-photos" />
              <Stat icon={<Mic size={12} />} label="Voice" n={context?.counts?.voice_notes} testid="sheet-asset-stat-voice" />
              <Stat icon={<ShieldAlert size={12} />} label="Conflicts" n={context?.counts?.conflicts} testid="sheet-asset-stat-conflicts" />
              <Stat icon={<MessageSquareWarning size={12} />} label="Stories" n={context?.counts?.stories} testid="sheet-asset-stat-stories" />
              <Stat icon={<Send size={12} />} label="Sends" n={context?.counts?.sends} testid="sheet-asset-stat-sends" />
              <Stat icon={<ListChecks size={12} />} label="Revs" n={context?.counts?.revisions} testid="sheet-asset-stat-revisions" />
            </div>
          </section>

          {/* Related stories list */}
          {(context?.stories || []).length > 0 && (
            <section className="mb-4" data-testid="sheet-asset-drawer-stories">
              <Heading icon={<MessageSquareWarning size={11} />} label="Related stories" />
              <ul className="text-[12px] space-y-1">
                {context.stories.slice(0, 6).map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center gap-2 px-2.5 py-2 rounded-md border border-[var(--fop-border)] bg-[var(--fop-surface-2)]/60"
                    data-testid={`sheet-asset-story-${s.id}`}
                  >
                    <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)] shrink-0">{s.status_label || s.status}</span>
                    <span className="flex-1 truncate text-[var(--fop-text-2)]">{s.people?.[0] || "—"}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Footer actions */}
          <div className="flex items-center gap-3 pt-3 mt-2 border-t border-[var(--fop-border)]">
            <a
              href={`/sheets/${a.sheet_id}/field-timeline`}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[12px] font-mono uppercase tracking-wider text-[var(--fop-text-2)] hover:text-white hover:bg-[var(--fop-surface-3)] transition-colors border border-[var(--fop-border-2)]"
              data-testid="sheet-asset-drawer-open-timeline"
            >
              <ExternalLink size={12} /> Open Field Timeline
            </a>
            <button
              type="button"
              className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[12px] font-mono uppercase tracking-wider text-[var(--st-escalated,#dc2626)] hover:bg-[var(--st-escalated,#dc2626)]/10 border border-[var(--st-escalated,#dc2626)]/40 hover:border-[var(--st-escalated,#dc2626)] transition-colors disabled:opacity-50"
              data-testid="sheet-asset-drawer-delete"
              disabled={busyDelete}
              onClick={async () => {
                if (busyDelete) return;
                if (!window.confirm(`Remove this ${a.asset_type_label || "asset"}?`)) return;
                setBusyDelete(true);
                try {
                  await api.delete(`/assets/${a.id}`);
                  confirm.raw?.success?.("Asset removed");
                  window.dispatchEvent(new Event("forgedops:assets-changed"));
                  onChanged?.();
                  onClose?.();
                } catch (e) {
                  confirm.raw?.error?.(e?.response?.data?.detail || "Couldn't remove the asset");
                } finally {
                  setBusyDelete(false);
                }
              }}
            >
              <Trash2 size={12} /> Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function Heading({ icon, label }) {
  return (
    <div className="flex items-center gap-1.5 mb-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-text-3)]">
      {icon}{label}
    </div>
  );
}

function Stat({ icon, label, n, testid }) {
  const zero = !n;
  return (
    <div
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-[var(--fop-border)] bg-[var(--fop-surface-2)]/50 ${zero ? "opacity-40" : ""}`}
      data-testid={testid}
    >
      <span className={zero ? "text-[var(--fop-text-3)]" : "text-[var(--fop-primary)]"}>{icon}</span>
      <span className={`font-mono tabular-nums text-[12px] ${zero ? "text-[var(--fop-text-3)]" : "text-[var(--fop-text)] font-bold"}`}>
        {n ?? 0}
      </span>
      <span className="text-[10px] uppercase tracking-wider text-[var(--fop-text-3)]">{label}</span>
    </div>
  );
}

function Row({ k, v }) {
  if (!v && v !== 0) return null;
  return (
    <>
      <dt className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)] pt-0.5">{k}</dt>
      <dd className="text-[var(--fop-text-2)] truncate">{v}</dd>
    </>
  );
}

function _fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}
